/**
 * Main entry point for QR Smart Menu SPA
 * Implements Proxy-based state management with reactive effects
 */

import { initAppData } from './utils/api.js';
import { initPWA } from './utils/pwa.js';
import { initI18n, setLanguage } from './utils/i18n.js';
import { initCurrency, setCurrency } from './utils/currency.js';
import { setCookie } from './utils/cookies.js';
import { ticker } from './utils/ScheduleTicker.js';

// Initialize global event bus FIRST
if (!globalThis[Symbol.for('APP_BUS')]) {
  globalThis[Symbol.for('APP_BUS')] = new EventTarget();
}

// State effects - automatically triggered on state changes
const stateEffects = {
  lang: (val) => {
    setCookie('lang', val, 30);
    document.documentElement.lang = val;
  },
  currency: (val) => {
    setCookie('currency', val, 30);
    console.log(`[Effect] Currency changed to ${val}`);
  },
  theme: (val) => {
    localStorage.setItem('app-theme', val);
    // Apply theme class to document
    document.documentElement.setAttribute('data-theme', val);
  },
  fontSize: (val) => {
    document.documentElement.style.setProperty('--main-size', `${val}px`);
  }
};

// Start app
init();

async function init() {
  console.log('[App] Initializing QR Smart Menu...');
  
  try {
    // 1. Start fetching data - this creates the Proxy state
    const { content: data, state } = await initAppData();
    
    console.log('[App] Data loaded, state proxy created');
    
    // 2. Setup state effects listeners
    setupStateEffects();
    
    // 3. Initialize i18n and currency (they will use the proxy)
    if (data.branch?.config) {
      initI18n(data.branch.config, state);
      initCurrency(data.branch.config, state);
    }
    
    // 4. Import and register components
    await import('./components/menu-frame.js');
    await import('./components/menu-app.js');
    await import('./components/menu-cover.js');
    await import('./components/menu-page.js');
    await import('./components/product-carousel.js');
    await import('./components/product-card.js');
    await import('./components/menu-context-sheet.js');
    await import('./components/menu-nav.js');
    await import('./components/menu-toast.js');
    await import('./components/menu-config.js');
    
    // 5. Render the app
    renderApp();
    
    // 6. Start other services
    ticker.start();
    initPWA();
    
    console.log('[App] Initialized successfully');
    
    // Dispatch ready event
    globalThis[Symbol.for('APP_BUS')].dispatchEvent(
      new CustomEvent('app:ready', { detail: data })
    );
    
  } catch (error) {
    console.error('[App] Initialization failed:', error);
    showError(error.message);
  }
}

function setupStateEffects() {
  const bus = globalThis[Symbol.for('APP_BUS')];
  
  // Listen for batch changes and apply effects
  bus.addEventListener('change:batch', (e) => {
    const changes = e.detail;
    console.log('[Effects] Batch changes:', changes);
    
    for (const [key, value] of Object.entries(changes)) {
      if (stateEffects[key]) {
        stateEffects[key](value);
      }
    }
  });
  
  // Also listen for individual changes (for components that need immediate response)
  Object.keys(stateEffects).forEach(key => {
    bus.addEventListener(`change:${key}`, (e) => {
      stateEffects[key](e.detail);
    });
  });
}

function renderApp() {
  const app = document.getElementById('app');
  if (app) {
    app.innerHTML = `
      <menu-frame>
        <menu-app></menu-app>
        <menu-context-sheet></menu-context-sheet>
        <menu-config></menu-config>
        <menu-nav></menu-nav>
        <menu-toast></menu-toast>
      </menu-frame>
    `;
  }
}

function showError(message) {
  const app = document.getElementById('app');
  if (app) {
    app.innerHTML = `
      <div style="
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        padding: 20px;
        text-align: center;
      ">
        <div style="font-size: 48px; margin-bottom: 16px;">⚠️</div>
        <h1 style="font-size: 20px; margin-bottom: 8px; color: #222;">
          No se pudo cargar el menú
        </h1>
        <p style="color: #666; margin-bottom: 20px;">
          ${message || 'Ha ocurrido un error'}
        </p>
        <button onclick="window.location.reload()" style="
          background: #007AFF;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 16px;
          cursor: pointer;
        ">
          Reintentar
        </button>
      </div>
    `;
  }
}
